import React, {useState, useEffect} from "react";
import { Box, Button, Column, Columns, Control, Field, Pagination, Table, Title } from "@allxsmith/bestax-bulma";
import {usePartOrdersData} from "../../hooks/usePartOrdersData";
import {useDispatch, useSelector} from "react-redux";
import {getDataFresh, getDepartmentsData, getPartOrdersData} from "../../state/selectors";
import LoadingSpinner from "../Utils/Spinner";
import PartOrderRow from "./PartOrderRow";
import SearchBar from "../Utils/SearchBar";
import FilterBlankHeading from "../Invoicing/FilterBlankHeading";
import FilterHeading from "../Invoicing/FilterHeading";
import useSelect from "../../hooks/useSelect";
import axios from "axios";
import {createSetDataFreshAction} from "../../actions/dataActions";
import {ToastContainer, toast} from "react-toastify";
import {useDepartmentsData} from "../../hooks/useDepartmentsData";
import DepartmentSelector from "../Settings/Departments/DepartmentSelector";
import FigureSummary from "../Figures/FigureSummary";
import Nav from "../Nav/Nav";
import PaginationWrapper from "../Utils/PaginationWrapper";


export default function PartOrderView(props){
    const dispatch = useDispatch();
    const dataFresh = useSelector(getDataFresh);

    const [searchText , setSearchText] = useState();
    const [currentSearch, setCurrentSearch] = useState();
    const [page, setPage] = useState(1);
    const [filters, setFilters] = useState({});
    const [selectedDepartment, setSelectedDepartment] = useState("BLANK");
    const [departmentDefaulted, setDepartmentDefaulted] = useState(false);
    // which dashboard tile is narrowing the list: {stage} or {tile}, null = none
    const [tileFilter, setTileFilter] = useState(null);
    const [importing, setImporting] = useState(false);
    const [counts, setCounts] = useState(null);

    useEffect(() => {
        axios.get("/api/partorder/counts")
            .then(r => setCounts(r.data || null))
            .catch(() => {});
    }, [dataFresh]);

    const importWebsiteOrders = () => {
        setImporting(true);
        axios.post("/api/partorder/import-website", {})
            .then(res => {
                const d = res.data || {};
                if (d.error) { toast.error("Import: " + d.error, {position: "top-right"}); return; }
                toast.success("Imported " + (d.created || 0) + " new, updated " + (d.updated || 0) + " (seen " + (d.seen || 0) + ")", {position: "top-right"});
                dispatch(createSetDataFreshAction(dataFresh + 1));
            })
            .catch(() => toast.error("Website import failed", {position: "top-right"}))
            .finally(() => setImporting(false));
    };

    const viewOptions = [
        {name:"ORDERS", value: "orders"},
        {name:"QUOTES", value: "quotes"},
        {name:"COMPLETE", value: "complete"},
        {name:"ENQUIRY", value: "enquiry"},
        {name:"ARCHIVED", value: "archived"}
    ];

    const [archiving, setArchiving] = useState(false);

    const archiveOldQuotes = () => {
        if (!window.confirm("Archive all quotes older than 6 months? They move to the Archived view (kept, not deleted).")) return;
        setArchiving(true);
        axios.post("/api/partorder/archive-quotes", {months: 6})
            .then(r => {
                toast.success("Archived " + ((r.data && r.data.archived) || 0) + " quote(s)", {position: "top-right"});
                dispatch(createSetDataFreshAction(dataFresh + 1));
            })
            .catch(() => toast.error("Archive failed", {position: "top-right"}))
            .finally(() => setArchiving(false));
    };

    const [view, viewInput, setView] = useSelect({
        label: "View",
        options: viewOptions,
        initialValue: "orders"
    });

    const handleSearch = () => {
        setPage(1);
        setCurrentSearch(searchText);
    };

    const clear = () => {
        setSearchText("");
        setCurrentSearch("");
        setPage(1);
    };


    const onDelete = data => {
        const conf = {
            url: props.endpoint+`partorder/${data.id}`,
            method: "delete"
        };
        axios(conf).then(res => {
            setPage(1);
            dispatch(createSetDataFreshAction(dataFresh + 1));
        }).catch(err => {
            toast.error("There was an error deleting that part order", {position: "top-right"});
        })
    }

    const onDepartmentChange = e => {
        setSelectedDepartment(e.target.value)
    }

    // clicking a tile shows just those orders; clicking the same tile again clears it
    const onTile = (newView, filter) => {
        setView(newView);
        setPage(1);
        setTileFilter(filter && tileFilter && JSON.stringify(filter) === JSON.stringify(tileFilter) ? null : filter);
    }
    const tileStyle = filter => ({
        cursor: "pointer",
        outline: filter && tileFilter && JSON.stringify(filter) === JSON.stringify(tileFilter) ? "3px solid #363636" : "none"
    });


    let params = {page: page}
    if(currentSearch){
        params.search = currentSearch;
    }
    if(view){
        params.view = view
    }
    if(selectedDepartment){
        params.department = selectedDepartment
    }
    if(tileFilter){
        Object.assign(params, tileFilter);
    }
    for (let filter in filters) {
        if (filters[filter][0] && filters[filter][0][filter]) {
          params[filter] = filters[filter][0][filter];
        }
    }

    const loaded = usePartOrdersData(params, dataFresh);
    const departmentsLoaded = useDepartmentsData({}, dataFresh);
    let data = useSelector(getPartOrdersData);
    const departments = useSelector(getDepartmentsData);

    // the parts office works Van Parts orders, so open on that department rather than an empty list
    useEffect(() => {
        if (departmentDefaulted || !departmentsLoaded || !departments) return;
        const list = Array.isArray(departments) ? departments : (departments.results || []);
        if (list.length === 0) return;   // not fetched yet - try again when it arrives
        const vpd = list.find(d => d && d.name && d.name.toLowerCase().includes("van parts"));
        if (vpd && selectedDepartment === "BLANK") setSelectedDepartment(String(vpd.id));
        setDepartmentDefaulted(true);
    }, [departmentsLoaded, departments, departmentDefaulted, selectedDepartment]);


    const headings = [
        {
            label: "Part Order Number",
            show: true
        },
        {
            label: "Date Created",
            date: true,
            show: true
        },
        {
            label: "Customer",
            field: "customer",
            labelField: "customer__display_name",
            show: true
        },
        {
            label: "Customer Registration",
            show: true
        },
        {
            label: "Customer VIN",
            show: true
        },
        {
            label: "Order Number",
            show: true
        },
        {
            label: "Description",
            show: true
        },
        {
            label: "Status",
            show: true
        },
        {
            label: "Date/Time Created",
            show: true
        },
        {
            label: "",
            show: view !== "complete"
        }
    ]




    if(!loaded || !departmentsLoaded){
        return (
            <div>
                <Nav/>
                <LoadingSpinner/>
            </div>
        )
    }



    // TODO(bestax-migrate): Form.Label became a plain element; the Bulma helper prop(s) `invisible` were dropped — restyle with classes
    // TODO(bestax-migrate): `showFirstLast` — no showFirstLast prop in bestax Pagination
    return (
        <div>
            <Nav/>
            <br/>
            <br/>
            <Box>
                <Title>Part Orders</Title>
                {counts ?
                    <>
                    <Columns isMultiline>
                        <Column>
                            <div className="notification is-danger" style={tileStyle({tile: "website"})}
                                 onClick={() => onTile("orders", {tile: "website"})}>
                                <p className="heading">Website orders</p>
                                <p className="title">{counts.website}</p>
                            </div>
                        </Column>
                        <Column>
                            <div className="notification is-danger" style={tileStyle(null)}
                                 onClick={() => onTile("enquiry", null)}>
                                <p className="heading">Inquiries</p>
                                <p className="title">{counts.inquiries}</p>
                            </div>
                        </Column>
                        <Column>
                            <div className="notification is-warning" style={tileStyle(null)}
                                 onClick={() => onTile("quotes", null)}>
                                <p className="heading">Quotations</p>
                                <p className="title">{counts.quotes}</p>
                            </div>
                        </Column>
                    </Columns>
                    <style>{"@keyframes vpdflash{0%,100%{opacity:1}50%{opacity:.4}} .vpd-flash{animation:vpdflash 1s infinite}"}</style>
                    {counts.stages ?
                    <Columns isMultiline>
                        {[
                            {k: "new", label: "New", color: "is-link"},
                            {k: "awaiting_reg_vin", label: "Awaiting Reg/VIN", color: "is-danger"},
                            {k: "processing", label: "Processing", color: "is-link"},
                            {k: "parts_awaiting_ordering", label: "Parts Awaiting Ordering", color: "is-warning"},
                            {k: "awaiting_parts", label: "Awaiting Parts", color: "is-warning"},
                            {k: "packing", label: "Packing", color: "is-info"},
                            {k: "dispatched", label: "Dispatched", color: "is-info"},
                            {k: "completed", label: "Completed", color: "is-success"},
                        ].map(t =>
                            <Column key={t.k}>
                                <div className={"notification " + t.color + ((t.k === "parts_awaiting_ordering" && counts.parts_awaiting_overdue > 0) ? " vpd-flash" : "")} style={tileStyle({stage: t.k})}
                                     onClick={() => onTile("orders", {stage: t.k})}>
                                    <p className="heading">{t.label}</p>
                                    <p className="title">{counts.stages[t.k] || 0}</p>
                                </div>
                            </Column>
                        )}
                        <Column>
                            <div className="notification is-warning" style={tileStyle({tile: "awaiting_payment"})}
                                 onClick={() => onTile("quotes", {tile: "awaiting_payment"})}>
                                <p className="heading">Awaiting Payment</p>
                                <p className="title">{counts.awaiting_payment || 0}</p>
                            </div>
                        </Column>
                        {/* 14 Oct (Neil): large-item delivery to price, then waiting for the customer to pay it */}
                        <Column>
                            <div className={"notification is-danger" + (counts.postage_to_quote > 0 ? " vpd-flash" : "")} style={tileStyle({tile: "postage_to_quote"})}
                                 onClick={() => onTile("orders", {tile: "postage_to_quote"})}>
                                <p className="heading">Postage To Quote</p>
                                <p className="title">{counts.postage_to_quote || 0}</p>
                            </div>
                        </Column>
                        <Column>
                            <div className="notification is-warning" style={tileStyle({tile: "awaiting_postage"})}
                                 onClick={() => onTile("orders", {tile: "awaiting_postage"})}>
                                <p className="heading">Awaiting Postage Payment</p>
                                <p className="title">{counts.awaiting_postage || 0}</p>
                            </div>
                        </Column>
                        <Column>
                            <div className="notification is-danger" style={tileStyle({tile: "backordered"})}
                                 onClick={() => onTile("orders", {tile: "backordered"})}>
                                <p className="heading">Back Ordered</p>
                                <p className="title">{counts.backordered || 0}</p>
                            </div>
                        </Column>
                        <Column>
                            <div className="notification is-warning" style={tileStyle({tile: "awaiting_reply"})}
                                 onClick={() => onTile("orders", {tile: "awaiting_reply"})}>
                                <p className="heading">Awaiting Reply</p>
                                <p className="title">{counts.awaiting_reply || 0}</p>
                            </div>
                        </Column>
                    </Columns>
                    : null}
                    </>
                    : null}
                <Columns isMultiline>
                    <Column>
                        <label className="label white">BLANK</label>
                        <SearchBar
                            searchText={searchText}
                            setSearchText={setSearchText}
                            handleSearch={handleSearch}
                            clear={clear}
                        />
                    </Column>
                    {viewInput}
                    <Column>
                        <DepartmentSelector
                            value={{id: selectedDepartment}}
                            options={departments}
                            onChange={onDepartmentChange}
                            blank={true}
                            editable={true}
                            label={"Department"}
                            name={"department"}
                        />
                    </Column>
                    {view === "quotes" ?
                        <Column>
                            <label className="label white">BLANK</label>
                            <Field>
                              <Control>
                                <Button
                                  onClick={archiveOldQuotes}
                                  color="warning"
                                  disabled={archiving}
                                  className="is-pulled-right"
                                >
                                  {archiving ? "Archiving..." : "Archive quotes > 6mo"}
                                </Button>
                              </Control>
                            </Field>
                        </Column>
                        : null}
                    <Column>
                        <label className="label white">BLANK</label>
                        <Field>
                          <Control>
                            <Button
                              onClick={importWebsiteOrders}
                              color="info"
                              disabled={importing}
                              className="is-pulled-right"
                            >
                              {importing ? "Importing..." : "Import Website Orders"}
                            </Button>
                          </Control>
                        </Field>
                    </Column>
                    <Column>
                        <label className="label white">BLANK</label>
                        <Field>
                          <Control>
                            <Button
                              onClick={() => window.open("/editpartorder", "_self")}
                              color="success"
                              className="is-pulled-right"
                            >
                              New Part Order +
                            </Button>
                          </Control>
                        </Field>
                    </Column>
                </Columns>
                <Columns isMultiline>
                    <Column>
                        <FigureSummary selectedDepartment={selectedDepartment}/>
                    </Column>
                </Columns>
                <Columns isMultiline>
                    <Column>
                        <Table isFullwidth>
                            <thead>
                                <tr>
                                    {headings.map(heading => {
                                        if (!heading.field && heading.show) {
                                          return <th key={heading.label}>{heading.label}</th>;
                                        }
                                        if (heading.blank) {
                                          return (
                                            <FilterBlankHeading
                                              key={heading.label}
                                              heading={heading}
                                              setFilters={f => {
                                                setPage(1);
                                                setFilters(f);
                                              }}
                                              filters={filters}
                                            />
                                          );
                                        }
                                        if(heading.show) {
                                            return (
                                                <FilterHeading
                                                    key={heading.label}
                                                    heading={heading}
                                                    setFilters={f => {
                                                        setPage(1);
                                                        setFilters(f);
                                                    }}
                                                    filters={filters}
                                                    options={data.filter_list[heading.field]}
                                                />
                                            );
                                        }
                                      })}
                                </tr>
                            </thead>
                            <tbody>
                            {data.results.map(item => (
                                <PartOrderRow
                                    partOrder={item}
                                    onDelete={onDelete}
                                    view={view}
                                />
                            ))}
                            </tbody>
                        </Table>
                        <PaginationWrapper
                            setPage={setPage}
                            page={page}
                            dataCount={Math.ceil(data.count / 25)}
                         ></PaginationWrapper>
                    </Column>
                </Columns>
            </Box>
        </div>
    );


}